/**
 * BulkBillingCodePage - Admin tool for bulk updating matter billing codes.
 *
 * Two-step workflow:
 * 1. Paste matter IDs + billing codes (CSV/tab-separated), click Preview
 * 2. Review changes, then click Apply to commit updates
 */

import {
  AlertTriangle,
  CheckCircle2,
  ClipboardPaste,
  Loader2,
  RotateCcw,
  Save,
  Search,
  XCircle,
} from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import type {
  BulkBillingCodeEntry,
  PreviewChangeItem,
  PreviewInvalidItem,
  PreviewResponse,
} from '@/services/api/bulkBillingCodeService';
import {
  useApplyBulkBillingCodeMutation,
  usePreviewBulkBillingCodeMutation,
} from '@/services/api/bulkBillingCodeService';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const VALID_BILLING_CODES = [
  'records_retrieval',
  'demand_only',
  'property_damage',
  'in_flight',
  'back_office',
  'full_pre_lit',
  'full_pre_lit_property_damage',
  'case_setup_only',
  'litigation_only',
];

const BILLING_CODE_LABELS: Record<string, string> = {
  records_retrieval: 'Records Retrieval',
  demand_only: 'Demand Only',
  property_damage: 'Property Damage',
  in_flight: 'In Progress Case',
  back_office: 'Back Office',
  full_pre_lit: 'Full Pre Lit',
  full_pre_lit_property_damage: 'Full Pre Lit + PD',
  case_setup_only: 'Case Setup Only',
  litigation_only: 'Litigation Only',
};

function billingLabel(code: string): string {
  return BILLING_CODE_LABELS[code] || code;
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

/** Parse pasted text into entries. Supports CSV, TSV, and space-separated. */
function parseInput(text: string): BulkBillingCodeEntry[] {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  return lines.reduce<BulkBillingCodeEntry[]>((entries, line) => {
    // Skip header-like rows
    if (/matter.?id/i.test(line) && /billing/i.test(line)) return entries;

    // Try comma, tab, then multiple spaces as delimiter
    let parts: string[];
    if (line.includes(',')) {
      parts = line.split(',').map((p) => p.trim());
    } else if (line.includes('\t')) {
      parts = line.split('\t').map((p) => p.trim());
    } else {
      parts = line.split(/\s+/);
    }

    if (parts.length >= 2) {
      const matter_id = parts[0].replace(/['"]/g, '');
      const new_billing_code = parts[1].replace(/['"]/g, '').toLowerCase();
      if (matter_id && new_billing_code) {
        entries.push({ matter_id, new_billing_code });
      }
    }

    return entries;
  }, []);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SummaryCard: React.FC<{
  label: string;
  count: number;
  color: 'green' | 'blue' | 'red' | 'amber' | 'zinc';
  icon: React.ReactNode;
}> = ({ label, count, color, icon }) => {
  const bg: Record<string, string> = {
    green: 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800',
    blue: 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800',
    red: 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800',
    amber:
      'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800',
    zinc: 'bg-zinc-50 border-zinc-200 dark:bg-zinc-900/20 dark:border-zinc-800',
  };
  return (
    <div className={`rounded-lg border px-4 py-3 ${bg[color]}`}>
      <div className="flex items-center gap-2 text-sm font-medium text-zinc-700 dark:text-zinc-300">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-zinc-900 dark:text-zinc-100">
        {count}
      </div>
    </div>
  );
};

const ChangesTable: React.FC<{ items: PreviewChangeItem[]; title: string }> = ({
  items,
  title,
}) => {
  if (items.length === 0) return null;
  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden">
      <div className="bg-zinc-50 dark:bg-zinc-800/50 px-4 py-2 border-b border-zinc-200 dark:border-zinc-700">
        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
          {title} ({items.length})
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50/50 dark:bg-zinc-800/30">
              <th className="px-4 py-2 text-left font-medium text-zinc-600 dark:text-zinc-400">
                Matter
              </th>
              <th className="px-4 py-2 text-left font-medium text-zinc-600 dark:text-zinc-400">
                Firm
              </th>
              <th className="px-4 py-2 text-left font-medium text-zinc-600 dark:text-zinc-400">
                Current Code
              </th>
              <th className="px-4 py-2 text-left font-medium text-zinc-600 dark:text-zinc-400">
                New Code
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.matter_id}
                className="border-b border-zinc-100 dark:border-zinc-800 last:border-b-0"
              >
                <td className="px-4 py-2">
                  <div className="font-medium text-zinc-900 dark:text-zinc-100">
                    {item.matter_name}
                  </div>
                  <div className="text-xs font-medium text-zinc-500 dark:text-zinc-500 font-mono">
                    {item.matter_id}
                  </div>
                </td>
                <td className="px-4 py-2 font-medium text-zinc-600 dark:text-zinc-400">
                  {item.firm_name}
                </td>
                <td className="px-4 py-2">
                  {item.current_billing_code ? (
                    <Badge color="zinc">{billingLabel(item.current_billing_code)}</Badge>
                  ) : (
                    <span className="font-medium text-zinc-500 dark:text-zinc-500 italic">
                      None
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <Badge color="blue">{billingLabel(item.new_billing_code)}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ErrorsTable: React.FC<{
  items: PreviewInvalidItem[];
  title: string;
}> = ({ items, title }) => {
  if (items.length === 0) return null;
  return (
    <div className="rounded-lg border border-red-200 dark:border-red-800 overflow-hidden">
      <div className="bg-red-50 dark:bg-red-900/20 px-4 py-2 border-b border-red-200 dark:border-red-800">
        <span className="text-sm font-medium text-red-700 dark:text-red-400">
          {title} ({items.length})
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-red-100 dark:border-red-900 bg-red-50/50 dark:bg-red-900/10">
              <th className="px-4 py-2 text-left font-medium text-red-600 dark:text-red-400">
                Matter ID
              </th>
              <th className="px-4 py-2 text-left font-medium text-red-600 dark:text-red-400">
                Billing Code
              </th>
              <th className="px-4 py-2 text-left font-medium text-red-600 dark:text-red-400">
                Reason
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr
                key={`${item.matter_id}-${idx}`}
                className="border-b border-red-50 dark:border-red-900/50 last:border-b-0"
              >
                <td className="px-4 py-2 font-medium font-mono text-xs text-zinc-700 dark:text-zinc-300">
                  {item.matter_id}
                </td>
                <td className="px-4 py-2 font-medium text-zinc-600 dark:text-zinc-400">
                  {item.new_billing_code}
                </td>
                <td className="px-4 py-2 font-medium text-red-600 dark:text-red-400">
                  {item.reason}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export const BulkBillingCodePage: React.FC = () => {
  const [inputText, setInputText] = React.useState('');
  const [preview, setPreview] = React.useState<PreviewResponse | null>(null);
  const [entries, setEntries] = React.useState<BulkBillingCodeEntry[]>([]);
  const [applied, setApplied] = React.useState(false);

  const [doPreview, { isLoading: isPreviewing }] =
    usePreviewBulkBillingCodeMutation();
  const [doApply, { isLoading: isApplying }] =
    useApplyBulkBillingCodeMutation();

  const handlePreview = async () => {
    const parsed = parseInput(inputText);
    if (parsed.length === 0) {
      toast.error('No valid entries found. Check your input format.');
      return;
    }
    setEntries(parsed);
    setApplied(false);

    try {
      const result = await doPreview({ entries: parsed }).unwrap();
      setPreview(result);
    } catch {
      toast.error('Failed to preview changes. Are you logged in as staff?');
    }
  };

  const handleApply = async () => {
    if (!preview || preview.changes.length === 0) return;

    try {
      const result = await doApply({ entries }).unwrap();
      if (result.updated > 0 && result.errors.length === 0) {
        toast.success(`Updated ${result.updated} matter(s)`);
        setApplied(true);
      } else if (result.updated > 0 && result.errors.length > 0) {
        toast.warning(
          `Updated ${result.updated} matter(s), but ${result.errors.length} failed`,
        );
        setApplied(true);
      } else {
        toast.error(
          `Failed to update: ${result.errors.length} error(s). All changes were rolled back.`,
        );
      }
    } catch {
      toast.error('Failed to apply changes.');
    }
  };

  const handleReset = () => {
    setInputText('');
    setPreview(null);
    setEntries([]);
    setApplied(false);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
          Bulk Billing Code Update
        </h1>
        <p className="mt-1 text-sm font-medium text-zinc-500 dark:text-zinc-400">
          Paste matter IDs and new billing codes below, preview the changes,
          then apply. Supports CSV, tab-separated, and space-separated formats.
        </p>
      </div>

      {/* Input section */}
      {!preview && (
        <div className="space-y-4">
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label
              htmlFor="billing-input"
              className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
            >
              Paste data (one row per matter: matter_id, new_billing_code)
            </label>
            <textarea
              id="billing-input"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={`e.g.\nd5b49790-602b-45ab-953f-be76bf5a3063, in_flight\n74c69fc0-038f-4302-85ee-539f76076879\tfull_pre_lit`}
              rows={12}
              className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-4 py-3 text-sm font-mono text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Valid codes reference */}
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 p-4">
            <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-2">
              Valid billing codes:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {VALID_BILLING_CODES.map((code) => (
                <Badge key={code} color="zinc">
                  {code}
                </Badge>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              color="blue"
              onClick={handlePreview}
              disabled={isPreviewing || !inputText.trim()}
              className="gap-1.5"
            >
              {isPreviewing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              {isPreviewing ? 'Previewing...' : 'Preview Changes'}
            </Button>
            <Button
              plain
              onClick={() => {
                navigator.clipboard.readText().then((text) => {
                  setInputText(text);
                  toast.success('Pasted from clipboard');
                }).catch(() => {
                  toast.error('Unable to read clipboard. Check browser permissions.');
                });
              }}
              className="gap-1.5"
            >
              <ClipboardPaste className="h-4 w-4" />
              Paste from Clipboard
            </Button>
          </div>
        </div>
      )}

      {/* Preview results */}
      {preview && (
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <SummaryCard
              label="Total Entries"
              count={preview.summary.total_entries}
              color="zinc"
              icon={<ClipboardPaste className="h-4 w-4" />}
            />
            <SummaryCard
              label="Will Change"
              count={preview.summary.will_change}
              color="blue"
              icon={<Save className="h-4 w-4" />}
            />
            <SummaryCard
              label="Already Correct"
              count={preview.summary.already_correct}
              color="green"
              icon={<CheckCircle2 className="h-4 w-4" />}
            />
            <SummaryCard
              label="Not Found"
              count={preview.summary.not_found}
              color="amber"
              icon={<AlertTriangle className="h-4 w-4" />}
            />
            <SummaryCard
              label="Invalid"
              count={
                preview.summary.invalid_codes + preview.summary.invalid_ids
              }
              color="red"
              icon={<XCircle className="h-4 w-4" />}
            />
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-3">
            {!applied && preview.changes.length > 0 && (
              <Button
                color="green"
                onClick={handleApply}
                disabled={isApplying}
                className="gap-1.5"
              >
                {isApplying ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {isApplying
                  ? 'Applying...'
                  : `Apply ${preview.changes.length} Change${preview.changes.length !== 1 ? 's' : ''}`}
              </Button>
            )}
            {applied && (
              <div className="flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                Changes applied successfully
              </div>
            )}
            <Button plain onClick={handleReset} className="gap-1.5">
              <RotateCcw className="h-4 w-4" />
              Start Over
            </Button>
          </div>

          {/* Changes table */}
          <ChangesTable items={preview.changes} title="Will Change" />

          {/* Already correct */}
          <ChangesTable
            items={preview.already_correct}
            title="Already Correct"
          />

          {/* Errors */}
          <ErrorsTable items={preview.not_found} title="Not Found" />
          <ErrorsTable
            items={preview.invalid_codes}
            title="Invalid Billing Codes"
          />
          <ErrorsTable items={preview.invalid_ids} title="Invalid IDs" />
        </div>
      )}
    </div>
  );
};
